// useQuickCommands.js
// Custom hook for managing Quick Commands state and operations

import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import API from './services/api';
import { COMMAND_TYPES, COMMAND_STATUS } from './quickCommandsConfig';

// Counter for generating unique command IDs
const commandCounters = {};

// Generate unique command ID (e.g., SP1, SP2, GE1)
const generateCommandId = (type) => {
  if (!commandCounters[type]) {
    commandCounters[type] = 0;
  }
  commandCounters[type] += 1;
  return `${type}${commandCounters[type]}`;
};

// Reset counters (useful when clearing all commands)
const resetCounters = () => {
  Object.keys(commandCounters).forEach((key) => {
    commandCounters[key] = 0;
  });
};

export const useQuickCommands = (options = {}) => {
  const queryClient = useQueryClient();
  const [commands, setCommands] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract options (e.g., debtFields for CC/CP commands)
  const { debtFields = [], debtFieldsSchema = {} } = options;

  // Get valid debt fields (excluding TotalDebt)
  const validDebtFields = debtFields.filter((f) => f !== 'TotalDebt');
  const singleDebtField = validDebtFields.length === 1 ? validDebtFields[0] : null;

  // Add a new command
  const addCommand = useCallback((type) => {
    const config = COMMAND_TYPES[type];
    if (!config) return null;

    // Build initial data with defaults
    const initialData = {};
    config.fields.forEach((field) => {
      if (field.default !== undefined) {
        initialData[field.name] = typeof field.default === 'function'
          ? field.default()
          : field.default;
      } else if (field.type === 'debt_field' && singleDebtField) {
        // Auto-populate debt_field when there's only one option
        initialData[field.name] = singleDebtField;
      } else {
        initialData[field.name] = '';
      }
    });

    const newCommand = {
      id: generateCommandId(type),
      type: type,
      status: COMMAND_STATUS.PENDING,
      error: null,
      data: initialData,
    };

    setCommands((prev) => [...prev, newCommand]);
    return newCommand.id;
  }, [singleDebtField]);

  // Update command data
  const updateCommand = useCallback((id, fieldName, value) => {
    setCommands((prev) =>
      prev.map((cmd) =>
        cmd.id === id
          ? { ...cmd, data: { ...cmd.data, [fieldName]: value }, error: null }
          : cmd
      )
    );
  }, []);

  // Remove a command
  const removeCommand = useCallback((id) => {
    setCommands((prev) => prev.filter((cmd) => cmd.id !== id));
  }, []);

  // Validate a single command
  const validateCommand = useCallback((command) => {
    const config = COMMAND_TYPES[command.type];
    if (!config) return { valid: false, errors: ['סוג פקודה לא ידוע'] };

    const errors = [];
    config.fields.forEach((field) => {
      // Check if field should be shown (conditional visibility)
      if (field.showWhen) {
        const conditionValue = command.data[field.showWhen.field];
        if (conditionValue !== field.showWhen.value) {
          return; // Skip validation for hidden fields
        }
      }

      // Required field validation
      if (field.required) {
        const value = command.data[field.name];
        if (value === undefined || value === null || value === '') {
          errors.push(`${field.label} הוא שדה חובה`);
        }
      }

      // Number validation
      if (field.type === 'number' && command.data[field.name] !== '') {
        const num = parseFloat(command.data[field.name]);
        if (isNaN(num) || num <= 0) {
          errors.push(`${field.label} חייב להיות מספר חיובי`);
        }
        if (field.min !== undefined && num < field.min) {
          errors.push(`${field.label} חייב להיות לפחות ${field.min}`);
        }
        if (field.max !== undefined && num > field.max) {
          errors.push(`${field.label} חייב להיות לכל היותר ${field.max}`);
        }
      }

      // Supplier validation
      if (field.type === 'supplier' && field.required) {
        const value = command.data[field.name];
        if (!value || value === '') {
          errors.push(`יש לבחור ${field.label}`);
        }
      }
    });

    return { valid: errors.length === 0, errors };
  }, []);

  // Submit a single command
  const submitSingleCommand = useCallback(
    async (command) => {
      const config = COMMAND_TYPES[command.type];
      if (!config) throw new Error('סוג פקודה לא ידוע');

      const payload = config.buildPayload(command.data);
      const response = await API.post(config.endpoint, payload);

      // Invalidate relevant queries
      config.queryKeysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });

      return response.data;
    },
    [queryClient]
  );

  // Submit all pending/failed commands
  const submitAll = useCallback(async () => {
    setIsSubmitting(true);
    const commandsToSubmit = commands.filter(
      (cmd) => cmd.status === COMMAND_STATUS.PENDING || cmd.status === COMMAND_STATUS.ERROR
    );

    const results = [];

    for (const command of commandsToSubmit) {
      // Update status to submitting
      setCommands((prev) =>
        prev.map((cmd) =>
          cmd.id === command.id
            ? { ...cmd, status: COMMAND_STATUS.SUBMITTING, error: null }
            : cmd
        )
      );

      // Validate first
      const validation = validateCommand(command);
      if (!validation.valid) {
        setCommands((prev) =>
          prev.map((cmd) =>
            cmd.id === command.id
              ? { ...cmd, status: COMMAND_STATUS.ERROR, error: validation.errors.join(', ') }
              : cmd
          )
        );
        results.push({ id: command.id, success: false, error: validation.errors.join(', ') });
        continue;
      }

      // Submit to API
      try {
        const data = await submitSingleCommand(command);
        setCommands((prev) =>
          prev.map((cmd) =>
            cmd.id === command.id
              ? { ...cmd, status: COMMAND_STATUS.SUCCESS, error: null }
              : cmd
          )
        );
        results.push({ id: command.id, success: true, data });
      } catch (err) {
        const errorMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'שגיאה לא ידועה';
        setCommands((prev) =>
          prev.map((cmd) =>
            cmd.id === command.id
              ? { ...cmd, status: COMMAND_STATUS.ERROR, error: errorMessage }
              : cmd
          )
        );
        results.push({ id: command.id, success: false, error: errorMessage });
      }
    }

    setIsSubmitting(false);
    return results;
  }, [commands, validateCommand, submitSingleCommand]);

  // Retry failed commands
  const retryFailed = useCallback(async () => {
    // Reset failed commands to pending
    setCommands((prev) =>
      prev.map((cmd) =>
        cmd.status === COMMAND_STATUS.ERROR
          ? { ...cmd, status: COMMAND_STATUS.PENDING, error: null }
          : cmd
      )
    );

    // Wait for state update then submit
    return new Promise((resolve) => {
      setTimeout(async () => {
        const results = await submitAll();
        resolve(results);
      }, 50);
    });
  }, [submitAll]);

  // Clear all commands
  const clearAll = useCallback(() => {
    setCommands([]);
    resetCounters();
  }, []);

  // Clear only successful commands
  const clearSuccessful = useCallback(() => {
    setCommands((prev) => prev.filter((cmd) => cmd.status !== COMMAND_STATUS.SUCCESS));
  }, []);

  // Summary statistics
  const summary = useMemo(
    () => ({
      total: commands.length,
      pending: commands.filter((c) => c.status === COMMAND_STATUS.PENDING).length,
      submitting: commands.filter((c) => c.status === COMMAND_STATUS.SUBMITTING).length,
      success: commands.filter((c) => c.status === COMMAND_STATUS.SUCCESS).length,
      error: commands.filter((c) => c.status === COMMAND_STATUS.ERROR).length,
    }),
    [commands]
  );

  // Check if there are any commands that can be submitted
  const canSubmit = useMemo(
    () =>
      commands.some(
        (cmd) => cmd.status === COMMAND_STATUS.PENDING || cmd.status === COMMAND_STATUS.ERROR
      ) && !isSubmitting,
    [commands, isSubmitting]
  );

  // Check if there are failed commands
  const hasFailed = useMemo(
    () => commands.some((cmd) => cmd.status === COMMAND_STATUS.ERROR),
    [commands]
  );

  // Check if there are successful commands
  const hasSuccessful = useMemo(
    () => commands.some((cmd) => cmd.status === COMMAND_STATUS.SUCCESS),
    [commands]
  );

  return {
    commands,
    isSubmitting,
    summary,
    canSubmit,
    hasFailed,
    hasSuccessful,
    addCommand,
    updateCommand,
    removeCommand,
    validateCommand,
    submitAll,
    retryFailed,
    clearAll,
    clearSuccessful,
  };
};

export default useQuickCommands;
